import { useCallback, useState } from "react";
import Popup from "@components/molecules/PopUp";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { messageType } from "@components/molecules/InputPopUp/types";
import { useTranslation } from "react-i18next";
import { addComma } from "@utils/product/productFormatters";

const useBulkInitScreenModals = (dynamicFormValues?: any, setValue?: any) => {
  const [modalType, setModalType] = useState<
    | "delete-total-price"
    | "delete-total-discount"
    | "reset-single-form"
    | "recipientEmail"
    | "recipientName"
    | "offer-total-discount"
    | null
  >(null);
  const [modalCallback, setModalCallback] = useState<(() => void) | null>(null);
  const { t } = useTranslation();

  const handlePress = () => {
    if (modalCallback) {
      modalCallback();
    }
    setModalType(null);
  };
  const handleRequestTotalPriceClose = () => {
    setValue(
      "offerTotalPrice",
      addComma(dynamicFormValues.totalOfferPrice.toString().replaceAll(",", ""))
    );
    setModalType(null);
  };

  const handleRequestDiscountPercentClose = () => {
    setValue(
      "offerTotalDiscount",
      dynamicFormValues.requestForm.offerTotalDiscount
    );
    setModalType(null);
  };

  const modalConfig = [
    {
      type: "delete-total-price",
      titleKey: "bulkRequest.deletePrice.modal.title.text",
      messageKey: "bulkRequest.deleteIndividual.modal.message.text",
      primaryButton: {
        titleKey: "bulkRequest.deleteIndividual.modal.button.primary.text",
        action: handlePress,
      },
      secondaryButton: {
        titleKey: "bulkRequest.deleteIndividual.modal.button.secondary.text",
        action: () => setModalType(null),
      },
    },
    {
      type: "delete-total-discount",
      titleKey: "bulkRequest.deleteTotalDiscount.modal.title.text",
      messageKey: "bulkRequest.deleteTotalDiscount.modal.message.text",
      primaryButton: {
        titleKey: "bulkRequest.deleteTotalDiscount.modal.button.primary.text",
        action: handlePress,
      },
      secondaryButton: {
        titleKey: "bulkRequest.deleteTotalDiscount.modal.button.secondary.text",
        action: () => setModalType(null),
      },
    },
    {
      type: "reset-single-form",
      titleKey: "bulkRequest.deleteIndividual.modal.title.text",
      messageKey: "bulkRequest.deletePrice.modal.message.text",
      primaryButton: {
        titleKey: "bulkRequest.deletePrice.modal.button.primary.text",
        action: handlePress,
      },
      secondaryButton: {
        titleKey: "bulkRequest.deletePrice.modal.button.secondary.text",
        action: handleRequestTotalPriceClose,
      },
    },
    {
      type: "recipientEmail",
      titleKey: "bulkRequest.recipientEmail.modal.title.text",
      messageKey: "bulkRequest.recipientEmail.modal.message.text",
      primaryButton: {
        titleKey: "bulkRequest.recipientEmail.modal.button.primary.text",
        action: handlePress,
      },
      secondaryButton: {
        titleKey: "bulkRequest.recipientEmail.modal.button.secondary.text",
        action: () => setModalType(null),
      },
    },
    {
      type: "recipientName",
      titleKey: "bulkRequest.recipientName.modal.title.text",
      messageKey: "bulkRequest.recipientName.modal.message.text",
      primaryButton: {
        titleKey: "bulkRequest.recipientName.modal.button.primary.text",
        action: handlePress,
      },
      secondaryButton: {
        titleKey: "bulkRequest.recipientName.modal.button.secondary.text",
        action: () => setModalType(null),
      },
    },
    {
      type: "offer-total-discount",
      titleKey: "bulkRequest.offerTotalDiscount.modal.title.text",
      messageKey: "bulkRequest.offerTotalDiscount.modal.message.text",
      primaryButton: {
        titleKey: "bulkRequest.offerTotalDiscount.modal.button.primary.text",
        action: handlePress,
      },
      secondaryButton: {
        titleKey: "bulkRequest.offerTotalDiscount.modal.button.secondary.text",
        action: handleRequestDiscountPercentClose,
      },
    },
  ];

  const renderModals = () => {
    const modal = modalConfig.find((m) => m.type === modalType);
    if (!modal) return null;

    return (
      <Popup
        type={messageType.Warning}
        title={t(modal.titleKey)}
        buttons={[
          {
            type: ButtonType.primary,
            title: t(modal.primaryButton.titleKey),
            onPressAction: modal.primaryButton.action,
            category: ButtonCategory.default,
          },
          {
            type: ButtonType.secondary,
            title: t(modal.secondaryButton.titleKey),
            onPressAction: modal.secondaryButton.action,
          },
        ]}
        HtmlSource={{
          html: t(modal.messageKey),
        }}
        styleKey="Body-R"
      />
    );
  };

  const showModal = useCallback(
    (value: typeof modalType, callbackFunction?: () => void) => {
      setModalType(value);
      if (callbackFunction) {
        setModalCallback(() => callbackFunction);
      }
    },
    []
  );

  return { renderModals, showModal };
};

export default useBulkInitScreenModals;
